import React, { FunctionComponent, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { LabelLarge, Input, Container, InputCont, InputBox, InputErrorMessage } from '../elements';
import formatPhone from '../helpers/formatPhone';
import sanitizePhoneNumber from '../helpers/sanitizePhoneNumber';
import AddressInput from '../Components/Shared/AddressInput';
import handleFormSubmit from '../actions/handleFormSubmit';
import { setContactFirstName, setContactLastName, setContactEmail, setContactPhone } from '../actions/application';
import {
  selectContactFirstName,
  selectContactLastName,
  selectContactEmail,
  selectContactPhone,
  selectMailingAddressStreet,
  selectContactEmailIsValid,
} from '../selectors/application';
import GhostButton from '../Components/Shared/GhostButton';
import { validatePhoneNumber } from '../helpers/inputValidation/validatePhoneNumber';
import { useValidateEmail } from '../helpers/inputValidation/validateEmail';
import styled from 'styled-components';
import { INPUT_ERROR_MSG } from '../constants';
import ValidationWarning from '../Components/Shared/ValidationWarning';
import { setMDALeadEmailSent } from '../actions/agent';
import { selectMDALeadEmailSent } from '../selectors/agent';

interface PropTypes {
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  mailingAddressStreet: string;
  setContactFirstName: (value: string) => void;
  setContactLastName: (value: string) => void;
  setContactEmail: (value: string) => void;
  setContactPhone: (value: string) => void;
  handleFormSubmit: () => void;
  setMDALeadEmailSent: (value: boolean) => void;
  emailIsValid: boolean;
  hasMDALeadEmailSent: boolean;
}

const Contact: FunctionComponent<PropTypes> = ({
  contactFirstName,
  contactLastName,
  contactEmail,
  contactPhone,
  setContactFirstName,
  setContactLastName,
  setContactEmail,
  setContactPhone,
  handleFormSubmit,
  emailIsValid,
  setMDALeadEmailSent,
  hasMDALeadEmailSent,
}) => {
  // Storing contact email locally in use state so that on change, we're not updating the redux store and only updating the redux store on blur
  // this is so that sendMDALeadEmail middleware will onl trigger on blur (see src/middleware/sendMDALeadEmail.ts)
  const [contactEmailLocal, setContactEmailLocal] = useState(contactEmail);
  const { validateEmail, emailValidationError, emailValidationWarning, hasEmailWarning } = useValidateEmail();

  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);

  useEffect(() => {
    document.title = 'Coterie - Contact';
    window.scrollTo(0, 0);
  }, []);

  const updateContactFirstName = (e: { target: HTMLInputElement }) => setContactFirstName(e.target.value);
  const updateContactLastName = (e: { target: HTMLInputElement }) => setContactLastName(e.target.value);
  const updateContactEmail = (e: { target: HTMLInputElement }) => setContactEmailLocal(e.target.value);
  const updateContactPhone = (e: { target: HTMLInputElement }) =>
    setContactPhone(sanitizePhoneNumber(e.target.value).slice(0, 10));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleFormSubmit();
  };

  const onPhoneNumberBlur = () => setIsPhoneNumberValid(validatePhoneNumber(contactPhone));
  const onEmailBlur = async () => {
    if (!contactEmailLocal) return;
    await validateEmail(contactEmailLocal);
    // if contact email gets updated, the MDA lead email should get resent with updated contact info
    if (hasMDALeadEmailSent) {
      setMDALeadEmailSent(false);
    }

    setContactEmail(contactEmailLocal);
  };

  // Adding a unique value to the autoComplete attribute to prevent browsers from auto filling inputs with saved autofill values
  // the quote flow contact page is not able to continue forward if auto filled values are inserted
  const autoCompleteTimestamp = Date.now();

  return (
    <Container>
      <form onSubmit={handleSubmit} autoComplete="off">
        <InputCont>
          <InputBox>
            <LabelLarge>First name</LabelLarge>
            <StyledInput
              value={contactFirstName}
              onChange={updateContactFirstName}
              data-cy="contact-first-name"
              aria-label="First Name"
              required
              aria-required
              type="text"
              autoComplete={`first-name-${autoCompleteTimestamp}`}
            />
          </InputBox>
          <InputBox>
            <LabelLarge>Last name</LabelLarge>
            <StyledInput
              value={contactLastName}
              onChange={updateContactLastName}
              data-cy="contact-last-name"
              aria-label="Last Name"
              required
              aria-required
              type="text"
              autoComplete={`last-name-${autoCompleteTimestamp}`}
            />
          </InputBox>
        </InputCont>
        <LabelLarge>Email</LabelLarge>
        <StyledInput
          value={contactEmailLocal}
          onChange={updateContactEmail}
          onBlur={onEmailBlur}
          hasError={emailIsValid === false}
          hasMessage={!emailIsValid || hasEmailWarning}
          data-cy="contact-email"
          aria-label="Email"
          type="email"
          required
          aria-required
          autoComplete={`contact-email-${autoCompleteTimestamp}`}
        />
        {!emailIsValid && emailValidationError && (
          <StyledInputErrorMessage>{emailValidationError}</StyledInputErrorMessage>
        )}
        {hasEmailWarning && (
          <ValidationWarning message={emailValidationWarning} collapsedMessage="View invalid email warning" />
        )}
        <LabelLarge>Phone</LabelLarge>
        <StyledInput
          value={formatPhone(contactPhone)}
          onChange={updateContactPhone}
          onBlur={onPhoneNumberBlur}
          hasError={!isPhoneNumberValid}
          data-cy="contact-phone"
          aria-label="Phone Number"
          customwidth="halfWithMargin"
          marginbottom="smallest"
          type="tel"
          required
          aria-required
          autoComplete={`phone-${autoCompleteTimestamp}`}
        />
        {!isPhoneNumberValid && (
          <StyledInputErrorMessage>{INPUT_ERROR_MSG.INVALID_PHONE_NUMBER}</StyledInputErrorMessage>
        )}
        <AddressInput />
        <GhostButton />
      </form>
    </Container>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  contactFirstName: selectContactFirstName(state),
  contactLastName: selectContactLastName(state),
  contactEmail: selectContactEmail(state),
  contactPhone: selectContactPhone(state),
  mailingAddressStreet: selectMailingAddressStreet(state),
  emailIsValid: selectContactEmailIsValid(state),
  hasMDALeadEmailSent: selectMDALeadEmailSent(state),
});

const mapDispatchToProps = {
  setContactFirstName,
  setContactLastName,
  setContactEmail,
  setContactPhone,
  handleFormSubmit,
  setMDALeadEmailSent,
};

export default connect(mapStateToProps, mapDispatchToProps)(Contact);

const StyledInput = styled(Input)<{ hasMessage?: boolean }>`
  margin-bottom: ${(props) => (props.hasMessage ? '8px' : '32px')};
  @media (max-width: 767px) {
    margin-bottom: ${(props) => (props.hasMessage ? '8px' : '12px')};
  }
`;

const StyledInputErrorMessage = styled(InputErrorMessage)`
  height: 16px;
  margin: 0 0 24px;
  @media (max-width: 767px) {
    margin: 0 0 12px;
  }
`;
